import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "wasp/client/auth";
import {
  getTradingSessionsByUser,
  getAllTradesForAnalytics,
  createManualTradingSession,
  updateTradingSession,
  getPendingSessionMerges,
  confirmSessionMerge,
  dismissSessionMerge,
  useQuery,
  useAction,
} from "wasp/client/operations";
import useTranslation from "../client/hooks/useTranslation";
import { DateRangePicker, type DateRange } from "../client/components/DateRangePicker";
import { presetToDateRange } from "../client/utils/dateRange";
import { useOnboardingStatus } from "../client/hooks/useOnboardingStatus";
import {
  getMoodEmoji,
  getPerformanceColor,
  getPerformanceLabel,
  getMoodLabel,
  formatSessionDuration,
  isTradeInSession,
} from "./sessionUtils";
import { Card, CardContent } from "../client/components/ui/card";
import { Button } from "../client/components/ui/button";
import { Input } from "../client/components/ui/input";
import { Label } from "../client/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../client/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../client/components/ui/dialog";
import { Textarea } from "../client/components/ui/textarea";
import { Eye, RotateCcw, Plus, LucideYoutube, Pencil, GitMerge, X } from "lucide-react";
import { toast } from "../client/hooks/use-toast";
import AddTradeModal from "../trading-journal/AddTradeModal";
import { SessionErrorSelect } from "./SessionErrorSelect";
import { SessionErrorBadges } from "./SessionErrorBadges";
import { BeamButton } from "../client/components/ui/beam-button";
import { motion } from "framer-motion";
import { PageTransition } from "../client/components/ui/motion";
function netPnl(trade: any): number {
  return (trade.pnl ?? 0) + (trade.commission ?? 0) + (trade.swaps ?? 0);
}

interface Filters {
  mood: string;
  performance: string;
  durationMin: string;
  durationMax: string;
}

const defaultFilters: Filters = {
  mood: "all",
  performance: "all",
  durationMin: "",
  durationMax: "",
};

interface AddSessionForm {
  startedAt: string;
  endedAt: string;
  mood: "excellent" | "good" | "neutral" | "poor" | "bad" | "";
  performance: "excellent" | "good" | "average" | "poor" | "";
  mentalState: string;
  notes: string;
  maxRunup: string;
  maxDrawdown: string;
  name: string;
  errorIds: string[];
}

const initialAddForm: AddSessionForm = {
  startedAt: "",
  endedAt: "",
  mood: "",
  performance: "",
  mentalState: "",
  notes: "",
  maxRunup: "",
  maxDrawdown: "",
  name: "",
  errorIds: [],
};

interface SessionsViewProps {
  // Mode données externes (mentor) — si fourni, skip les queries internes
  externalSessions?: any[];
  externalTrades?: any[];
  externalPendingMerges?: any[];
  externalIsLoading?: boolean;
  // Mode
  readOnly?: boolean;
  // Callbacks actions (uniquement en mode non-readOnly)
  onRefetch?: () => void;
  onRefetchMerges?: () => void;
  // Quand fourni, redirige vers la page détail mentor au lieu de /session/:id
  studentId?: string;
}

export function SessionsView({
  externalSessions,
  externalTrades,
  externalPendingMerges,
  externalIsLoading,
  readOnly = false,
  onRefetch,
  onRefetchMerges,
  studentId,
}: SessionsViewProps) {
  const t = useTranslation();
  const navigate = useNavigate();
  const { data: user } = useAuth();
  const { steps } = useOnboardingStatus(user?.id);
  const sessionStep = steps.find((s) => s.id === "session-detail");
  const showOnboardingBanner =
    !readOnly &&
    sessionStep !== undefined &&
    !sessionStep.completed &&
    !(user as any)?.onboardingCompletedAt;

  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [dateRange, setDateRange] = useState<DateRange>(() =>
    presetToDateRange(readOnly ? "all_time" : "last_30_days")
  );
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [addForm, setAddForm] = useState<AddSessionForm>(initialAddForm);
  const [isAddTradeModalOpen, setIsAddTradeModalOpen] = useState(false);
  const [addedTradesCount, setAddedTradesCount] = useState(0);
  const [editNameSessionId, setEditNameSessionId] = useState<string | null>(null);
  const [editNameValue, setEditNameValue] = useState("");

  // Internal queries — disabled when external data is provided
  const { data: sessionsRaw = [], refetch } = useQuery(getTradingSessionsByUser, undefined, {
    enabled: !externalSessions,
  });
  const { data: tradesRaw = [] } = useQuery(
    getAllTradesForAnalytics,
    { excludeInactive: false },
    { enabled: !externalSessions }
  );
  const { data: pendingMergesRaw = [], refetch: refetchMerges } = useQuery(
    getPendingSessionMerges,
    undefined,
    { enabled: !externalSessions && !readOnly }
  );

  // Merge external data with internal query data
  const sessions = (externalSessions !== undefined ? externalSessions : sessionsRaw) as any[];
  const tradesData = (externalTrades !== undefined ? externalTrades : tradesRaw) as any[];
  const pendingMerges = (
    externalPendingMerges !== undefined ? externalPendingMerges : pendingMergesRaw
  ) as any[];

  // Actions — always instantiated, only called in non-readOnly mode
  const createManualSessionFn = useAction(createManualTradingSession);
  const updateSessionFn = useAction(updateTradingSession);
  const confirmMergeFn = useAction(confirmSessionMerge);
  const dismissMergeFn = useAction(dismissSessionMerge);

  // P&L and distinct account count per session. A session spans EVERY account
  // traded in its window: without the count, a trader who imported a single CSV
  // reads the other accounts' trades as phantom data.
  const { sessionPnlMap, sessionAccountCountMap } = useMemo(() => {
    const pnlMap = new Map<string, number>();
    const accountCountMap = new Map<string, number>();
    const closedSessions = sessions.filter((s) => !s.isActive && s.startedAt && s.endedAt);
    for (const session of closedSessions) {
      let pnl = 0;
      const accountIds = new Set<string>();
      for (const trade of tradesData) {
        // Attach trades to the session by their ENTRY time (see isTradeInSession).
        if (isTradeInSession(trade, session)) {
          pnl += netPnl(trade);
          if (trade.accountId) accountIds.add(trade.accountId);
        }
      }
      pnlMap.set(session.id, parseFloat(pnl.toFixed(2)));
      accountCountMap.set(session.id, accountIds.size);
    }
    return { sessionPnlMap: pnlMap, sessionAccountCountMap: accountCountMap };
  }, [sessions, tradesData]);

  const activeSessions = useMemo(() => sessions.filter((s) => s.isActive), [sessions]);

  const filtered = useMemo(() => {
    return sessions
      .filter((s) => !s.isActive && s.endedAt)
      .filter((s) => {
        if (dateRange.from && s.startedAt && new Date(s.startedAt) < dateRange.from)
          return false;
        if (dateRange.to && s.startedAt && new Date(s.startedAt) > dateRange.to)
          return false;
        if (filters.mood && filters.mood !== "all" && s.mood !== filters.mood) return false;
        if (
          filters.performance &&
          filters.performance !== "all" &&
          s.performance !== filters.performance
        )
          return false;
        if (filters.durationMin && (s.actualDuration ?? 0) < parseInt(filters.durationMin))
          return false;
        if (filters.durationMax && (s.actualDuration ?? 0) > parseInt(filters.durationMax))
          return false;
        return true;
      })
      .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  }, [sessions, filters, dateRange]);

  const completedSessions = sessions.filter((s) => !s.isActive && s.endedAt);
  const hasFilters =
    filters.mood !== "all" ||
    filters.performance !== "all" ||
    filters.durationMin !== "" ||
    filters.durationMax !== "";

  function resetFilters() {
    setFilters(defaultFilters);
  }

  function doRefetch() {
    if (onRefetch) {
      onRefetch();
    } else {
      refetch();
    }
  }

  function doRefetchMerges() {
    if (onRefetchMerges) {
      onRefetchMerges();
    } else {
      refetchMerges();
    }
  }

  async function handleConfirmMerge(id: string) {
    if (readOnly) return;
    try {
      await confirmMergeFn({ id });
      doRefetch();
      doRefetchMerges();
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    }
  }

  async function handleDismissMerge(id: string) {
    if (readOnly) return;
    try {
      await dismissMergeFn({ id });
      doRefetchMerges();
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    }
  }

  const handleSaveName = async () => {
    if (!editNameSessionId || readOnly) return;
    try {
      await (updateSessionFn as any)({ id: editNameSessionId, name: editNameValue.trim() || null });
      doRefetch();
      setEditNameSessionId(null);
    } catch (error: any) {
      toast({ title: t.common.error, description: error.message, variant: "destructive" });
    }
  };

  function updateFilter<K extends keyof Filters>(key: K, value: Filters[K]) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  const handleAddSession = async () => {
    if (readOnly) return;
    try {
      await createManualSessionFn({
        startedAt: addForm.startedAt ? new Date(addForm.startedAt) : new Date(),
        endedAt: addForm.endedAt ? new Date(addForm.endedAt) : new Date(),
        mood: addForm.mood as any,
        performance: addForm.performance as any,
        mentalState: addForm.mentalState || undefined,
        notes: addForm.notes || undefined,
        maxRunup: addForm.maxRunup !== "" ? parseFloat(addForm.maxRunup) : undefined,
        maxDrawdown: addForm.maxDrawdown !== "" ? parseFloat(addForm.maxDrawdown) : undefined,
        name: addForm.name.trim() || undefined,
        errorIds: addForm.errorIds,
      });

      toast({ title: t.session.sessionEnded });
      setIsAddDialogOpen(false);
      setAddForm(initialAddForm);
      doRefetch();
    } catch (error: any) {
      toast({ title: t.common.error, description: error.message, variant: "destructive" });
    }
  };

  return (
    <PageTransition className="w-full py-10">
      <motion.div
        className="mb-6 flex items-center justify-between"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
      >
        <div>
          <h1 className="text-3xl font-bold">{t.sessions.title}</h1>
          <p className="text-muted-foreground mt-2">{t.sessions.subtitle}</p>
        </div>
        {!readOnly && (
          <BeamButton size="default" onClick={() => setIsAddDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t.sessions.addSession}
          </BeamButton>
        )}
      </motion.div>

      {showOnboardingBanner && (
        <div className="mb-6 rounded-lg border border-primary/30 bg-primary/10 p-4 flex items-center gap-3">
          <span className="text-xl">⏱️</span>
          <p className="text-sm text-foreground font-medium">{t.sessions.onboardingBanner}</p>
        </div>
      )}

      {!readOnly && pendingMerges.length > 0 && (
        <div className="mb-6 rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-4">
          <div className="flex items-center gap-2 mb-3">
            <GitMerge className="h-4 w-4 text-yellow-400" />
            <span className="font-medium text-yellow-400">{t.sessions.pendingMergeTitle}</span>
          </div>
          <p className="text-sm text-muted-foreground mb-3">{t.sessions.pendingMergeSubtitle}</p>
          <div className="flex flex-col gap-2">
            {pendingMerges.map((merge: any) => (
              <div key={merge.id} className="flex items-center justify-between rounded-md bg-card p-3">
                <span className="text-sm text-foreground">
                  {t.sessions.pendingMergeBanner
                    .replace(
                      "{start}",
                      new Date(merge.proposedStart).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    )
                    .replace(
                      "{end}",
                      new Date(merge.proposedEnd).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    )
                    .replace("{count}", String(merge.tradeCount))}
                </span>
                <div className="flex gap-2">
                  <Button size="sm" variant="default" onClick={() => handleConfirmMerge(merge.id)}>
                    {t.sessions.pendingMergeAccept}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDismissMerge(merge.id)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Filter bar */}
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex flex-wrap gap-4 items-end">
              {/* Date range */}
              <DateRangePicker value={dateRange} onChange={setDateRange} />

              {/* Mood filter */}
              <div className="flex flex-col gap-1 min-w-[160px]">
                <Label className="text-xs text-muted-foreground">{t.sessions.filterMood}</Label>
                <Select value={filters.mood} onValueChange={(v) => updateFilter("mood", v)}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t.sessions.allMoods}</SelectItem>
                    <SelectItem value="excellent">
                      {getMoodEmoji("excellent")} {getMoodLabel("excellent", t)}
                    </SelectItem>
                    <SelectItem value="good">
                      {getMoodEmoji("good")} {getMoodLabel("good", t)}
                    </SelectItem>
                    <SelectItem value="neutral">
                      {getMoodEmoji("neutral")} {getMoodLabel("neutral", t)}
                    </SelectItem>
                    <SelectItem value="poor">
                      {getMoodEmoji("poor")} {getMoodLabel("poor", t)}
                    </SelectItem>
                    <SelectItem value="bad">
                      {getMoodEmoji("bad")} {getMoodLabel("bad", t)}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Performance filter */}
              <div className="flex flex-col gap-1 min-w-[160px]">
                <Label className="text-xs text-muted-foreground">
                  {t.sessions.filterPerformance}
                </Label>
                <Select
                  value={filters.performance}
                  onValueChange={(v) => updateFilter("performance", v)}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t.sessions.allPerformances}</SelectItem>
                    <SelectItem value="excellent">
                      <span className={getPerformanceColor("excellent")}>
                        {getPerformanceLabel("excellent", t)}
                      </span>
                    </SelectItem>
                    <SelectItem value="good">
                      <span className={getPerformanceColor("good")}>
                        {getPerformanceLabel("good", t)}
                      </span>
                    </SelectItem>
                    <SelectItem value="average">
                      <span className={getPerformanceColor("average")}>
                        {getPerformanceLabel("average", t)}
                      </span>
                    </SelectItem>
                    <SelectItem value="poor">
                      <span className={getPerformanceColor("poor")}>
                        {getPerformanceLabel("poor", t)}
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Duration min */}
              <div className="flex flex-col gap-1 min-w-[130px]">
                <Label className="text-xs text-muted-foreground">
                  {t.sessions.filterDurationMin}
                </Label>
                <Input
                  type="number"
                  min={0}
                  value={filters.durationMin}
                  onChange={(e) => updateFilter("durationMin", e.target.value)}
                  className="h-8 text-sm"
                  placeholder="0"
                />
              </div>

              {/* Duration max */}
              <div className="flex flex-col gap-1 min-w-[130px]">
                <Label className="text-xs text-muted-foreground">
                  {t.sessions.filterDurationMax}
                </Label>
                <Input
                  type="number"
                  min={0}
                  value={filters.durationMax}
                  onChange={(e) => updateFilter("durationMax", e.target.value)}
                  className="h-8 text-sm"
                  placeholder="∞"
                />
              </div>

              {/* Reset */}
              {hasFilters && (
                <div className="flex flex-col justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={resetFilters}
                    className="h-8 flex items-center gap-1.5"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    {t.sessions.filterReset}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {/* Empty state — no sessions at all */}
            {completedSessions.length === 0 && activeSessions.length === 0 ? (
              <div className="py-16 text-center text-muted-foreground text-sm px-6">
                {t.sessions.noSessions}
              </div>
            ) : filtered.length === 0 && activeSessions.length === 0 ? (
              /* Empty state — filters active but no match */
              <div className="py-16 text-center text-muted-foreground text-sm px-6">
                {t.sessions.noSessionsFiltered}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        {t.sessions.colName}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        {t.sessions.colDate}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        {t.sessions.colDuration}
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        {t.sessions.colMood}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        {t.sessions.colPerformance}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        {t.sessions.colErrors}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        {t.sessions.colRunup}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        {t.sessions.colDrawdown}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        {t.sessions.colPnl}
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        {t.sessions.colVideo}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide w-12" />
                    </tr>
                  </thead>
                  <tbody>
                    {/* Active sessions first */}
                    {activeSessions.map((session) => (
                      <tr
                        key={session.id}
                        onClick={() =>
                          studentId
                            ? navigate(`/mentor/student/${studentId}/session/${session.id}`)
                            : navigate(`/session/${session.id}`)
                        }
                        className="border-b hover:bg-muted/30 cursor-pointer transition-colors bg-primary/5"
                      >
                        {/* Name */}
                        <td
                          className="px-4 py-3 text-sm max-w-[180px]"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-center gap-2">
                            {/* Pulsing red dot */}
                            <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
                            </span>
                            <span
                              className={`truncate ${
                                (session as any).name
                                  ? "text-foreground"
                                  : "text-muted-foreground/40"
                              }`}
                            >
                              {(session as any).name || t.sessions.activeSession}
                            </span>
                          </div>
                        </td>
                        {/* Date */}
                        <td className="px-4 py-3 text-sm text-foreground whitespace-nowrap">
                          {session.startedAt
                            ? new Date(session.startedAt).toLocaleDateString(undefined, {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })
                            : "—"}
                        </td>
                        {/* Duration */}
                        <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                          <span className="italic">{t.sessions.inProgress}</span>
                        </td>
                        {/* Mood */}
                        <td
                          className="px-4 py-3 text-center text-lg"
                          title={getMoodLabel(session.mood, t)}
                        >
                          {getMoodEmoji(session.mood)}
                        </td>
                        {/* Performance */}
                        <td className="px-4 py-3 text-sm">
                          <span className="text-muted-foreground">—</span>
                        </td>
                        {/* Errors */}
                        <td className="px-4 py-3">
                          <SessionErrorBadges errors={(session as any).errors} />
                        </td>
                        {/* Runup */}
                        <td className="px-4 py-3 text-sm text-right">
                          <span className="text-muted-foreground">—</span>
                        </td>
                        {/* Drawdown */}
                        <td className="px-4 py-3 text-sm text-right">
                          <span className="text-muted-foreground">—</span>
                        </td>
                        {/* PnL */}
                        <td className="px-4 py-3 text-sm text-right">
                          <span className="text-muted-foreground">—</span>
                        </td>
                        {/* Video */}
                        <td className="px-4 py-3 text-center">
                          <span className="text-muted-foreground/30">—</span>
                        </td>
                        {/* Eye */}
                        <td
                          className="px-4 py-3 text-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={() =>
                              studentId
                                ? navigate(`/mentor/student/${studentId}/session/${session.id}`)
                                : navigate(`/session/${session.id}`)
                            }
                            className="inline-flex items-center justify-center p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                            title={t.sessions.viewDetail}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}

                    {/* Completed sessions */}
                    {filtered.map((session) => {
                      const pnl =
                        session.startedAt && session.endedAt
                          ? sessionPnlMap.get(session.id)
                          : undefined;
                      const accountCount = sessionAccountCountMap.get(session.id) ?? 0;

                      return (
                        <tr
                          key={session.id}
                          onClick={() =>
                            studentId
                              ? navigate(`/mentor/student/${studentId}/session/${session.id}`)
                              : navigate(`/session/${session.id}`)
                          }
                          className="border-b last:border-b-0 hover:bg-muted/30 cursor-pointer transition-colors"
                        >
                          {/* Name */}
                          <td
                            className="px-4 py-3 text-sm max-w-[180px]"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center gap-1 group">
                              <span
                                className={`truncate ${
                                  (session as any).name
                                    ? "text-foreground"
                                    : "text-muted-foreground/40"
                                }`}
                              >
                                {(session as any).name || "—"}
                              </span>
                              {(session as any).isAutoGenerated && (
                                <span className="ml-2 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                                  {t.sessions.autoBadge}
                                </span>
                              )}
                              {!readOnly && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditNameSessionId(session.id);
                                    setEditNameValue((session as any).name ?? "");
                                  }}
                                  className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-all flex-shrink-0"
                                  title={t.sessions.editNameTitle}
                                >
                                  <Pencil className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </td>

                          {/* Date */}
                          <td className="px-4 py-3 text-sm text-foreground whitespace-nowrap">
                            {session.startedAt
                              ? new Date(session.startedAt).toLocaleDateString(undefined, {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                })
                              : "—"}
                            {accountCount > 1 && (
                              <div className="text-xs text-muted-foreground">
                                {t.sessions.accountsCount.replace("{count}", String(accountCount))}
                              </div>
                            )}
                          </td>

                          {/* Duration */}
                          <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                            {formatSessionDuration(session.actualDuration)}
                          </td>

                          {/* Mood */}
                          <td
                            className="px-4 py-3 text-center text-lg"
                            title={getMoodLabel(session.mood, t)}
                          >
                            {getMoodEmoji(session.mood)}
                          </td>

                          {/* Performance */}
                          <td className="px-4 py-3 text-sm">
                            {session.performance ? (
                              <span
                                className={`font-semibold ${getPerformanceColor(
                                  session.performance
                                )}`}
                              >
                                {getPerformanceLabel(session.performance, t)}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>

                          {/* Errors */}
                          <td className="px-4 py-3">
                            <SessionErrorBadges errors={(session as any).errors} />
                          </td>

                          {/* Max Run-up */}
                          <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                            {session.maxRunup != null ? (
                              <span className="text-pnlPositive">
                                ▲ {(session.maxRunup as number).toFixed(2)} $
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>

                          {/* Max Drawdown */}
                          <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                            {session.maxDrawdown != null ? (
                              <span className="text-pnlNegative">
                                ▼ {Math.abs(session.maxDrawdown as number).toFixed(2)} $
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>

                          {/* P&L */}
                          <td className="px-4 py-3 text-sm text-right font-medium whitespace-nowrap">
                            {pnl !== undefined ? (
                              <span className={pnl >= 0 ? "text-pnlPositive" : "text-pnlNegative"}>
                                {pnl >= 0 ? "+" : ""}
                                {pnl.toFixed(2)} $
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>

                          {/* Video indicator */}
                          <td className="px-4 py-3 text-center">
                            {(session as any).videoUrl ? (
                              <LucideYoutube className="w-4 h-4 text-primary inline-block" />
                            ) : (
                              <span className="text-muted-foreground/30">—</span>
                            )}
                          </td>

                          {/* Eye icon */}
                          <td
                            className="px-4 py-3 text-right"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              onClick={() =>
                                studentId
                                  ? navigate(`/mentor/student/${studentId}/session/${session.id}`)
                                  : navigate(`/session/${session.id}`)
                              }
                              className="inline-flex items-center justify-center p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                              title={t.sessions.viewDetail}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Session Dialog — hidden in readOnly mode */}
      {!readOnly && (
        <>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t.sessions.addSession}</DialogTitle>
                <DialogDescription>{t.session.evaluation}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Name */}
                <div>
                  <Label htmlFor="sessionName">{t.sessions.colName}</Label>
                  <Input
                    id="sessionName"
                    value={addForm.name}
                    onChange={(e) =>
                      setAddForm({ ...addForm, name: e.target.value.slice(0, 100) })
                    }
                    placeholder={t.sessions.editNamePlaceholder}
                    maxLength={100}
                  />
                  <p className="text-xs text-muted-foreground mt-1">{addForm.name.length}/100</p>
                </div>

                {/* Timing */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="startedAt">{t.sessionDetail.startedAt}</Label>
                    <Input
                      id="startedAt"
                      type="datetime-local"
                      value={addForm.startedAt}
                      onChange={(e) => setAddForm({ ...addForm, startedAt: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="endedAt">{t.sessionDetail.endedAt}</Label>
                    <Input
                      id="endedAt"
                      type="datetime-local"
                      value={addForm.endedAt}
                      onChange={(e) => setAddForm({ ...addForm, endedAt: e.target.value })}
                    />
                  </div>
                </div>

                {/* Mood + Performance */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="mood">{t.session.mood} *</Label>
                    <Select
                      value={addForm.mood}
                      onValueChange={(value: AddSessionForm["mood"]) =>
                        setAddForm({ ...addForm, mood: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t.session.moodPlaceholder} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="excellent">{t.session.moodExcellent}</SelectItem>
                        <SelectItem value="good">{t.session.moodGood}</SelectItem>
                        <SelectItem value="neutral">{t.session.moodNeutral}</SelectItem>
                        <SelectItem value="poor">{t.session.moodPoor}</SelectItem>
                        <SelectItem value="bad">{t.session.moodBad}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="performance">{t.session.performance} *</Label>
                    <Select
                      value={addForm.performance}
                      onValueChange={(value: AddSessionForm["performance"]) =>
                        setAddForm({ ...addForm, performance: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t.session.performancePlaceholder} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="excellent">{t.session.perfExcellent}</SelectItem>
                        <SelectItem value="good">{t.session.perfGood}</SelectItem>
                        <SelectItem value="average">{t.session.perfAverage}</SelectItem>
                        <SelectItem value="poor">{t.session.perfPoor}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="mentalState">{t.session.mentalState}</Label>
                  <Input
                    id="mentalState"
                    value={addForm.mentalState}
                    onChange={(e) => setAddForm({ ...addForm, mentalState: e.target.value })}
                    placeholder={t.session.mentalStatePlaceholder}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="maxRunup">{t.session.maxRunup}</Label>
                    <Input
                      id="maxRunup"
                      type="number"
                      step="0.01"
                      value={addForm.maxRunup}
                      onChange={(e) => setAddForm({ ...addForm, maxRunup: e.target.value })}
                      placeholder={t.session.maxRunupPlaceholder}
                    />
                  </div>
                  <div>
                    <Label htmlFor="maxDrawdown">{t.session.maxDrawdown}</Label>
                    <Input
                      id="maxDrawdown"
                      type="number"
                      step="0.01"
                      value={addForm.maxDrawdown}
                      onChange={(e) => setAddForm({ ...addForm, maxDrawdown: e.target.value })}
                      placeholder={t.session.maxDrawdownPlaceholder}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes">{t.session.notes}</Label>
                  <Textarea
                    id="notes"
                    value={addForm.notes}
                    onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })}
                    placeholder={t.session.notesPlaceholder}
                    rows={4}
                  />
                </div>

                {/* Errors */}
                <div>
                  <Label>{t.session.errors}</Label>
                  <SessionErrorSelect
                    value={addForm.errorIds}
                    onChange={(next) => setAddForm({ ...addForm, errorIds: next })}
                  />
                </div>

                {/* Section trades */}
                <div className="pt-2 border-t border-border/50">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-semibold">{t.sessionDetail.trades}</Label>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="gap-1.5 h-7 text-xs"
                      onClick={() => setIsAddTradeModalOpen(true)}
                    >
                      <Plus className="w-3.5 h-3.5" />
                      {t.sessionDetail.addTrade}
                    </Button>
                  </div>
                  {addedTradesCount > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {addedTradesCount} trade{addedTradesCount > 1 ? "s" : ""} ajouté
                      {addedTradesCount > 1 ? "s" : ""}
                    </p>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsAddDialogOpen(false);
                    setAddForm(initialAddForm);
                    setAddedTradesCount(0);
                  }}
                >
                  {t.common.cancel}
                </Button>
                <Button
                  onClick={handleAddSession}
                  disabled={!addForm.mood || !addForm.performance}
                >
                  {t.sessions.addSession}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* AddTradeModal — nested, trades créés immédiatement en DB */}
          <AddTradeModal
            open={isAddTradeModalOpen}
            onOpenChange={setIsAddTradeModalOpen}
            onImportDone={() => {
              setIsAddTradeModalOpen(false);
              setAddedTradesCount((c) => c + 1);
            }}
          />

          {/* Edit Name Dialog */}
          <Dialog
            open={!!editNameSessionId}
            onOpenChange={(open) => {
              if (!open) setEditNameSessionId(null);
            }}
          >
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>{t.sessions.editNameTitle}</DialogTitle>
              </DialogHeader>
              <div className="py-2 space-y-2">
                <Input
                  value={editNameValue}
                  onChange={(e) => setEditNameValue(e.target.value.slice(0, 100))}
                  placeholder={t.sessions.editNamePlaceholder}
                  maxLength={100}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveName();
                  }}
                  autoFocus
                />
                <p className="text-xs text-muted-foreground text-right">
                  {editNameValue.length}/100
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditNameSessionId(null)}>
                  {t.common.cancel}
                </Button>
                <Button onClick={handleSaveName}>{t.sessions.editNameSave}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </PageTransition>
  );
}
